import { Box, Typography } from "@mui/material";
import React from "react";
import { useChat } from "../../../context/ChatProvider";
import { MessageProps } from "../../../interfaces/all";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import { MessageContainer, MessageContent, MessageSender } from "./styles";

interface ButtonMessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const ButtonMessage: React.FC<ButtonMessagePropsSkeleton> = React.memo(
  ({ message, showAvatar = true, showDate = true, showSkeleton = false }) => {
    const isFromClient = message.sentBy === "client";
    const showTriangle = showAvatar;
    const { selectedConversation } = useChat();
    const currentReadStatus = message.readStatus || { isRead: false };

    const getSenderName = React.useCallback(() => {
      const formatName = (name: string) => {
        return name
          .split(" ")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ");
      };

      let name: string;
      switch (message.sentBy) {
        case "client":
          name =
            selectedConversation?.valid_name ||
            selectedConversation?.name ||
            "Cliente";
          return formatName(name);
        case "user":
          name = selectedConversation?.attendant_name || "Atendente";
          return formatName(name);
        default:
          return "Usuário";
      }
    }, [
      message.sentBy,
      selectedConversation?.valid_name,
      selectedConversation?.name,
      selectedConversation?.attendant_name,
    ]);

    if (showSkeleton) {
      return (
        <MessageContainer
          isFromClient={isFromClient}
          showTriangle={showTriangle}
        >
          {isFromClient ? (
            <>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minHeight: 40,
                  }}
                >
                  <Box
                    sx={{
                      width: "60%",
                      height: 20,
                      bgcolor: "grey.300",
                      borderRadius: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: "40%",
                      height: 16,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </MessageContent>
            </>
          ) : (
            <>
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minHeight: 40,
                  }}
                >
                  <Box
                    sx={{
                      width: "60%",
                      height: 20,
                      bgcolor: "grey.300",
                      borderRadius: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: "40%",
                      height: 16,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </MessageContent>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            </>
          )}
        </MessageContainer>
      );
    }

    const renderButtonContent = () => {
      let buttonText = "";
      let buttonPayload = "";

      // Primeiro, tentar extrair dados do content.button (estrutura nova)
      if ((message.content as any)?.button) {
        buttonText = (message.content as any).button.title || "";
        buttonPayload = (message.content as any).button.id || "";
      }

      // Se não encontrou, tentar extrair do rawWebhookData (estrutura antiga)
      if (!buttonText && message.rawWebhookData) {
        const rawData = message.rawWebhookData as any;

        // Tentar estrutura interactive.button_reply
        if (
          rawData?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.interactive
            ?.button_reply
        ) {
          const buttonReply =
            rawData.entry[0].changes[0].value.messages[0].interactive
              .button_reply;
          buttonText = buttonReply.title || "";
          buttonPayload = buttonReply.id || "";
        }

        // Tentar estrutura button direta (mais comum)
        if (
          !buttonText &&
          rawData?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.button
        ) {
          const buttonData =
            rawData.entry[0].changes[0].value.messages[0].button;
          buttonText = buttonData.text || "";
          buttonPayload = buttonData.payload || "";
        }
      }

      // Se ainda não encontrou, usar o texto do content como fallback
      if (!buttonText && message.content?.text) {
        buttonText = message.content.text;
      }

      // Se ainda não tem texto, mostrar uma mensagem padrão
      if (!buttonText) {
        buttonText = "Botão clicado";
      }

      return (
        <MessageContainer
          isFromClient={isFromClient}
          showTriangle={showTriangle}
        >
          {isFromClient ? (
            <>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: isFromClient ? "#e3f2fd" : "#f3e5f5",
                      color: isFromClient ? "#1976d2" : "#7b1fa2",
                      border: `1px solid ${isFromClient ? "#1976d2" : "#7b1fa2"}`,
                      borderRadius: "16px",
                      padding: "6px 12px",
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      maxWidth: "100%",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      alignSelf: "flex-start",
                    }}
                  >
                    {buttonText}
                  </Box>
                  {buttonPayload && buttonPayload !== buttonText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem", opacity: 0.7 }}
                    >
                      ID: {buttonPayload}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "2px",
                      minWidth: "100%",
                    }}
                  >
                    <MessageMetaInfo
                      message={message}
                      showDate={showDate}
                      isFromClient={isFromClient}
                      readStatus={currentReadStatus}
                      sx={{
                        position: "static",
                        right: "auto",
                        bottom: "auto",
                        height: "auto",
                        minWidth: "auto",
                      }}
                    />
                  </Box>
                </Box>
              </MessageContent>
            </>
          ) : (
            <>
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: isFromClient ? "#e3f2fd" : "#f3e5f5",
                      color: isFromClient ? "#1976d2" : "#7b1fa2",
                      border: `1px solid ${isFromClient ? "#1976d2" : "#7b1fa2"}`,
                      borderRadius: "16px",
                      padding: "6px 12px",
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      maxWidth: "100%",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      alignSelf: isFromClient ? "flex-start" : "flex-end",
                    }}
                  >
                    {buttonText}
                  </Box>
                  {buttonPayload && buttonPayload !== buttonText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        opacity: 0.7,
                        textAlign: isFromClient ? "left" : "right",
                      }}
                    >
                      ID: {buttonPayload}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "2px",
                      minWidth: "100%",
                    }}
                  >
                    <MessageMetaInfo
                      message={message}
                      showDate={showDate}
                      isFromClient={isFromClient}
                      readStatus={currentReadStatus}
                      sx={{
                        position: "static",
                        right: "auto",
                        bottom: "auto",
                        height: "auto",
                        minWidth: "auto",
                      }}
                    />
                  </Box>
                </Box>
              </MessageContent>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            </>
          )}
        </MessageContainer>
      );
    };

    return renderButtonContent();
  },
);

ButtonMessage.displayName = "ButtonMessage";

export default ButtonMessage;
